import * as Colors from '@brightlayer-ui/colors';
import { Box, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { chartsTooltipClasses } from '@mui/x-charts/ChartsTooltip';
import React from 'react';

export type BarChartDataItem = { id: string | number; name: string; value: number; tooltip?: string };

type BarChartProps = {
    title: string;
    height: number;
    dataset: BarChartDataItem[];
    units?: string;
    color: string;
    'data-testid'?: string;
};

export const BasicBarChart: React.FC<BarChartProps> = ({
    title,
    height,
    dataset,
    color,
    units,
    'data-testid': testId,
}) => (
    <Box data-testid={testId}>
        <Box position="relative">
            <Typography color={Colors.gray[800]} variant="h6" component="div" sx={{ mb: 2 }}>
                {title}
            </Typography>
            <BarChart
                dataset={dataset}
                xAxis={[
                    {
                        dataKey: 'id',
                        categoryGapRatio: 0.5,
                        disableTicks: true,
                        disableLine: true,
                        valueFormatter: (id: string | number, context: { location: string }): string => {
                            const entry = dataset.find((d) => d.id === id);
                            if (!entry) return '';
                            return context.location === 'tick' ? entry.name : entry.tooltip ?? entry.name;
                        },
                    },
                ]}
                series={[
                    {
                        dataKey: 'value',
                        color: color,
                        valueFormatter: (value: number | null): string =>
                            value !== null ? `${value.toString()} ${units ?? ''}` : '',
                    },
                ]}
                height={height}
                yAxis={[
                    {
                        disableTicks: true,
                        disableLine: true,
                        min: 0,
                        max: dataset.every((d) => d.value === 0) ? 5 : undefined,
                    },
                ]}
                grid={{ vertical: false, horizontal: true }}
                slotProps={{
                    tooltip: {
                        sx: {
                            [`&.${chartsTooltipClasses.root} .${chartsTooltipClasses.labelCell}`]: {
                                display: 'none',
                            },
                        },
                    },
                }}
            />
        </Box>
    </Box>
);
